'use client';

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faFolder,
  faChevronRight,
  faChevronDown,
  faFileAlt,
  faInbox,
} from '@fortawesome/free-solid-svg-icons';
import styles from './ProfessionalSidebar.module.css';
import SearchBar from '../shared-modules/SearchBar';

export default function ProfessionalSidebar({
  headings = [],
  activeHeadingSlug = null,
  onNavigate = () => {},
}) {
  const [expandedSections, setExpandedSections] = useState(() => {
    const first = headings?.find(h => h.level === 1)?.slug;
    return first ? new Set([first]) : new Set();
  });

  const [search, setSearch] = useState('');
  const contentRefs = useRef({});

  const buildHierarchy = () => {
    const sections = [];
    let currentH1 = null;
    let currentH2 = null;

    headings.forEach(h => {
      if (h.level === 1) {
        currentH1 = { ...h, children: [] };
        sections.push(currentH1);
        currentH2 = null;
      } else if (h.level === 2 && currentH1) {
        currentH2 = { ...h, children: [] };
        currentH1.children.push(currentH2);
      } else if (h.level === 3 && currentH2) {
        currentH2.children.push(h);
      }
    });

    return sections;
  };

  const rawSections = useMemo(buildHierarchy, [headings]);

  const sections = useMemo(() => {
    if (!search.trim()) return rawSections;

    const q = search.toLowerCase();
    const toExpand = new Set();

    const filtered = rawSections
      .map(sec => {
        const matchSection = sec.text.toLowerCase().includes(q);

        const filterChildren = (children = []) =>
          children
            .map(ch => {
              const matchChild = ch.text.toLowerCase().includes(q);
              const sub = filterChildren(ch.children);
              if (matchChild || sub.length) {
                toExpand.add(sec.slug); 

                return { ...ch, children: sub };
              }
              return null;
            })
            .filter(Boolean);

        const newChildren = filterChildren(sec.children);

        if (matchSection || newChildren.length) {
          toExpand.add(sec.slug); 

          return { ...sec, children: newChildren };
        }
        return null;
      })
      .filter(Boolean);

    setExpandedSections(prev => new Set([...Array.from(prev), ...Array.from(toExpand)]));
    return filtered;
  }, [search, rawSections]);

  const toggleSection = useCallback((slug) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });
  }, []);

  const setRef = (slug) => (el) => {
    contentRefs.current[slug] = el;
  };

  const renderSubsection = (sub, depth = 1, parentSlug = null) => {
    const hasChildren = sub.children?.length > 0;

    const handleClick = () => {
      if (parentSlug) {
        onNavigate(`${parentSlug}#${sub.slug.replace(`${parentSlug}#`, '')}`);
      } else {
        onNavigate(sub.slug);
      }
    };

    const subSlugNormalized = sub.slug || '';
    const isSubActive = Boolean(activeHeadingSlug && (
      activeHeadingSlug === subSlugNormalized ||
      activeHeadingSlug.endsWith(`#${subSlugNormalized.split('#').pop()}`) ||
      activeHeadingSlug === `${parentSlug}#${subSlugNormalized.split('#').pop()}`
    ));

    return (
      <div key={sub.slug} className={styles.subsection}>
        <button
          type="button"
          className={`${styles.subsectionButton} ${isSubActive ? styles.active : ''}`}
          onClick={handleClick}
          style={{ paddingLeft: `${depth * 16 + 12}px` }}
          title={sub.text}
        >
          <span className={styles.pageIcon}><FontAwesomeIcon icon={faFileAlt} /></span>
          <span className={styles.subsectionTitle}>{sub.text}</span>
        </button>

        {hasChildren && (
          <div className={styles.subsubsections}>
            {sub.children.map(item =>
              renderSubsection(item, depth + 1, parentSlug || sub.slug)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (section) => {
    const isExpanded = expandedSections.has(section.slug);
    const hasChildren = section.children?.length > 0;

    const measuredEl = contentRefs.current[section.slug];
    const maxHeight = isExpanded
      ? measuredEl ? `${measuredEl.scrollHeight}px` : 'none'
      : '0px';

    const isSectionActive = Boolean(activeHeadingSlug && (
      activeHeadingSlug === section.slug ||
      activeHeadingSlug.startsWith(`${section.slug}#`)
    ));

    return (
      <div key={section.slug} className={styles.section}>
        <div className={styles.sectionRow}>
          {hasChildren ? (
            <button
              type="button"
              className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}
              onClick={() => toggleSection(section.slug)}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
            </button>
          ) : (
            <span className={styles.expandPlaceholder} />
          )}

          <button
            type="button"
            className={`${styles.sectionButtonInner} ${isSectionActive ? styles.active : ''}`}
            onClick={() => onNavigate(section.slug)}
            title={section.text}
          >
            <span className={styles.folderIcon}>
              <FontAwesomeIcon icon={hasChildren ? faFolder : faFileAlt} />
            </span>
            <span className={styles.sectionTitle}>{section.text}</span>
          </button>
        </div>

        {hasChildren && (
          <div
            ref={setRef(section.slug)}
            className={styles.childrenWrapper}
            style={{ maxHeight }}
          >
            <div className={styles.childrenInner}>
              {section.children.map(sub => renderSubsection(sub, 1, section.slug))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.header}>
        <SearchBar
          searchTerm={search}
          onSearchChange={setSearch}
          placeholder="Search sections"
          width="250px"
        />
      </div>

      <nav className={styles.nav}>
        {sections.length > 0 ? (
          sections.map(renderSection)
        ) : (
          <div className={styles.empty}>
            <FontAwesomeIcon icon={faInbox} /> No sections found
          </div>
        )}
      </nav>
    </aside>
  );
}

